'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { usePlanner } from '../plannerContext';
import { programColor, shortCode, type PlannerProgram, type PlannerRequirement } from '../plannerModel';
import { CreditMeter, SlotMark, SlotRow } from '../PlannerPrimitives';

/** Finished means earned, not merely planned: a planned course can still slip. */
function isRequirementDone(req: PlannerRequirement) {
  return req.mode === 'courses' ? req.slots.length > 0 && req.slots.every((slot) => slot.state === 'earned') : req.creditsEarned >= req.creditsNeeded;
}

/** One quiet line for a finished requirement; open it from the Semesters board or Completion options if needed. */
function CompletedRequirementLine({ req }: { req: PlannerRequirement }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-xs text-[var(--ss-text-muted)]">
      <span className="flex min-w-0 items-center gap-2"><span aria-hidden className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--ss-positive)] text-[9px] text-white">✓</span><span className="truncate">{req.title}</span></span>
      <span className="shrink-0 tabular-nums">{req.mode === 'courses' ? `${req.slots.length} of ${req.slots.length}` : `${req.creditsEarned} cr`}</span>
    </div>
  );
}

function RequirementBlock({ req }: { req: PlannerRequirement }) {
  const { open, nameOf, listLabel } = usePlanner();
  const filled = req.slots.filter((slot) => slot.state !== 'open');
  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className={`text-[13.5px] font-semibold leading-snug ${req.openCount ? '' : 'text-[var(--ss-text-muted)]'}`}>{req.title}</h4>
          {req.description ? <p className="mt-0.5 line-clamp-2 text-xs text-[var(--ss-text-muted)]" title={req.description}>{req.description}</p> : null}
        </div>
        <span className="flex-none text-xs tabular-nums text-[var(--ss-text-muted)]">
          {req.mode === 'courses' ? `${req.slots.length - req.openCount} of ${req.slots.length}` : `${req.creditsEarned + req.creditsPlanned} of ${req.creditsNeeded} cr`}
        </span>
      </div>
      {req.mode === 'courses' ? <SlotRow slots={req.slots} size="lg" /> : <CreditMeter earned={req.creditsEarned} planned={req.creditsPlanned} needed={req.creditsNeeded} />}
      <div className="flex flex-wrap gap-1.5">
        {filled.map((slot, i) => {
          const label = slot.code ? nameOf(slot.code) : listLabel(slot.listName || '');
          return (
            <button
              key={i}
              type="button"
              onClick={(e) => slot.code ? open({ kind: 'course', code: slot.code, anchor: e.currentTarget }) : open({ kind: 'slot', requirementKey: req.key, anchor: e.currentTarget })}
              className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-[var(--ss-surface-subtle)] px-2 py-1 text-left text-xs ring-1 ring-inset ring-[var(--ss-border)] hover:ring-[var(--ss-text-muted)]"
            >
              <SlotMark slot={slot} />
              <span className="truncate font-medium">{label}</span>
              {slot.code ? <span className="font-mono text-[10.5px] text-[var(--ss-text-muted)]">{shortCode(slot.code)}</span> : null}
              {slot.state === 'planned' && slot.termName ? <span className="whitespace-nowrap text-[10.5px] font-semibold text-[var(--ss-warning)]">{slot.termName}</span> : null}
            </button>
          );
        })}
        {req.openCount ? (
          <button type="button" onClick={(e) => open({ kind: 'slot', requirementKey: req.key, anchor: e.currentTarget })}
            className="rounded-lg border-[1.5px] border-dashed border-[var(--ss-text-muted)]/50 px-2.5 py-1 text-xs font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]">
            + Fill {req.openCount > 1 ? `${req.openCount} open slots` : 'open slot'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ProgramPanel({ program }: { program: PlannerProgram }) {
  const { completedMode } = usePlanner();
  const { needed, earned, planned, open } = program.slotTotals;
  const doneCount = program.requirements.filter(isRequirementDone).length;
  const pct = (n: number) => (needed ? (n / needed) * 100 : 0);
  return (
    <section className="overflow-hidden rounded-2xl border border-[var(--ss-border)] bg-[var(--ss-surface)]" aria-labelledby={`program-${program.degreeId}`}>
      <header className="flex flex-col gap-3 border-b border-[var(--ss-border)] p-4 sm:p-5" style={{ boxShadow: `inset 4px 0 0 ${programColor(program.programIndex)}` }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id={`program-${program.degreeId}`} className="text-lg font-bold leading-tight">{program.degreeName}</h3>
            <p className="mt-1 text-sm text-[var(--ss-text-muted)]">
              {program.captured
                ? <><b className="text-[var(--ss-text)]">{earned}</b> earned · <b className="text-[var(--ss-text)]">{planned}</b> planned · <b className="text-[var(--ss-text)]">{open}</b> open, across {program.requirements.length} requirements{doneCount && completedMode !== 'show' ? ` · ${doneCount} finished ${completedMode === 'hide' ? 'hidden' : 'collapsed'}` : ''}</>
                : 'Scarlet Sync hasn’t captured this program’s requirements yet, so nothing here is counted.'}
            </p>
          </div>
          <Link href={`/explore?view=degrees&degree=${program.degreeId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ss-brand)] hover:underline">
            Program details <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
        {program.captured ? (
          <span className="flex h-2.5 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)] ring-1 ring-inset ring-[var(--ss-border)]" role="img" aria-label={`${earned} earned, ${planned} planned, ${open} open of ${needed} requirement slots`}>
            <span className="bg-[var(--ss-positive)]" style={{ width: `${pct(earned)}%` }} />
            <span className="planner-slot-planned" style={{ width: `${pct(planned)}%` }} />
          </span>
        ) : null}
      </header>
      {program.captured ? (
        <div className="grid gap-x-8 px-4 sm:px-5 xl:grid-cols-2">
          {program.sections.map((section) => (
            <div key={section.key} className="border-b border-[var(--ss-border)] py-3 last:border-b-0 xl:[&:nth-last-child(2)]:border-b-0">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--ss-text-muted)]">{section.title}</h3>
                {section.logic === 'or' ? <span className="text-[11px] font-semibold text-[var(--ss-info)]">Choose one path</span> : null}
              </div>
              <div className="divide-y divide-[var(--ss-border)]">
                {section.requirements.map((req) => !isRequirementDone(req) || completedMode === 'show'
                  ? <RequirementBlock key={req.key} req={req} />
                  : completedMode === 'compact' ? <CompletedRequirementLine key={req.key} req={req} /> : null)}
                {completedMode === 'hide' && section.requirements.every(isRequirementDone) ? <p className="py-2 text-xs text-[var(--ss-text-muted)]">All {section.requirements.length} requirement{section.requirements.length === 1 ? '' : 's'} here are complete.</p> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <p className="border-t border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-4 py-2.5 text-[11px] text-[var(--ss-text-muted)] sm:px-5">
        Built from the requirement tree captured in Scarlet Sync. Grade minimums, residency and course-reuse limits aren&apos;t evaluated; only an advisor can certify the degree.
      </p>
    </section>
  );
}

export default function DegreeView() {
  const { programs: allPrograms, actions, programScope } = usePlanner();
  const programs = programScope === null ? allPrograms : allPrograms.filter((program) => program.degreeId === programScope);
  if (!programs.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--ss-border)] p-8 text-center">
        <p className="font-semibold">No programs in this plan yet</p>
        <p className="mt-1 text-sm text-[var(--ss-text-muted)]">Add a major, minor or certificate to see its requirements as slots you can fill.</p>
        <button type="button" onClick={actions.importDegree} className="mt-4 rounded-lg bg-[var(--ss-brand)] px-4 py-2 text-sm font-semibold text-[var(--ss-brand-contrast)]">Add a program</button>
      </div>
    );
  }
  return <div className="flex flex-col gap-5">{programs.map((program) => <ProgramPanel key={program.degreeId} program={program} />)}</div>;
}
